'use client';

import { useCallback, useEffect, useState } from 'react';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { usePathname } from 'next/navigation';

import { Calendar, Menu, Package, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { persistLanguage } from '~/lib/i18n/persist-language';

import { NetAcademyHeaderAccountSection } from './header-account-section';
import { useSession } from '~/lib/hooks/use-header-auth';

/**
 * EN / AR pill toggle. Click switches to the other supported locale and
 * triggers a full page reload so server-rendered content (and the `dir`
 * attribute) re-renders with the new language. Designed for dark navbars.
 */
function NetLanguageToggle({ className }: { className?: string }) {
  const { i18n } = useTranslation();

  const supported = (i18n.options.supportedLngs as string[] | undefined)?.filter(
    (l) => l.toLowerCase() !== 'cimode',
  ) ?? ['en'];

  const current = i18n.language?.split('-')[0] ?? 'en';
  const next = supported.find((l) => l !== current) ?? current;

  const handleClick = useCallback(async () => {
    await persistLanguage(i18n, next);
    window.location.reload();
  }, [i18n, next]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`Switch language to ${next.toUpperCase()}`}
      className={cn(
        'inline-flex h-10 items-center gap-1.5 rounded-full border border-white/30 px-4 text-xs font-semibold tracking-wider text-white/85 transition-colors duration-200 hover:border-[var(--net-lime)] hover:text-[var(--net-lime)]',
        className,
      )}
    >
      <span
        className={cn(
          current === 'en'
            ? 'text-white'
            : 'text-white/40',
        )}
      >
        EN
      </span>
      <span className="text-white/30" aria-hidden>
        /
      </span>
      <span
        className={cn(
          current === 'ar'
            ? 'text-white'
            : 'text-white/40',
        )}
      >
        AR
      </span>
    </button>
  );
}

const navLinks = [
  { labelKey: 'landing:nav.about', fallback: 'About', href: '/#about' },
  { labelKey: 'landing:nav.programs', fallback: 'Programs', href: '/#programs' },
  {
    labelKey: 'landing:nav.facilities',
    fallback: 'Facilities',
    href: '/#facilities',
  },
  { labelKey: 'landing:nav.location', fallback: 'Location', href: '/#location' },
];

const authDesktopNavLinks = [
  { labelKey: 'landing:nav.programs', fallback: 'Programs', href: '/#programs' },
  { labelKey: 'landing:nav.location', fallback: 'Location', href: '/#location' },
];

const authNavLinks = [
  {
    labelKey: 'landing:nav.myBookings',
    fallback: 'My Bookings',
    href: '/bookings',
    icon: Calendar,
  },
  {
    labelKey: 'landing:nav.myPackages',
    fallback: 'My Packages',
    href: '/packages',
    icon: Package,
  },
];

const solidNavbarRoutes = [
  '/book',
  '/auth',
  '/bookings',
  '/profile',
  '/packages',
  '/payment',
  '/contact',
  '/faq',
  '/event',
  '/coaches',
  '/membership',
  '/privacy-policy',
  '/terms-of-service',
];

export function NetAcademyHeader() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const session = useSession();
  const isAuthenticated = !!session.data;

  const isLandingPage = pathname === '/';
  const shouldBeSolid = solidNavbarRoutes.some((route) =>
    pathname?.startsWith(route),
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  return (
    <>
      <header
        className={cn(
          'fixed left-0 right-0 top-0 z-50 transition-all duration-500',
          mobileMenuOpen
            ? 'bg-[var(--net-ink)] py-3'
            : shouldBeSolid || scrolled
              ? 'bg-[var(--net-blue)]/95 py-3 backdrop-blur-md'
              : 'bg-transparent py-5',
        )}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-12">
          <Link href="/" className="relative z-10 flex-shrink-0">
            <Image
              src="/landing-pages/netacademy/images/logo-header-white.svg"
              alt="NET Tennis Academy"
              width={140}
              height={50}
              className="h-9 w-auto lg:h-11"
              priority
            />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex xl:gap-10">
            {(isAuthenticated ? authDesktopNavLinks : navLinks).map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="font-net-body whitespace-nowrap text-sm font-semibold tracking-wide text-white/85 transition-colors duration-300 hover:text-[var(--net-lime)]"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated &&
              authNavLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="font-net-body whitespace-nowrap text-sm font-semibold tracking-wide text-white/85 transition-colors duration-300 hover:text-[var(--net-lime)]"
                >
                  {t(link.labelKey, { defaultValue: link.fallback })}
                </Link>
              ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <NetLanguageToggle />
            <NetAcademyHeaderAccountSection />
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="-mr-2 p-2 text-white lg:hidden"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {mobileMenuOpen && (
          <div
            className="fixed inset-0 top-[60px] z-40 bg-black/60 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
        )}

        <div
          className={cn(
            'absolute left-0 right-0 top-full z-50 overflow-hidden bg-[var(--net-ink)] transition-all duration-300 lg:hidden',
            mobileMenuOpen ? 'max-h-[600px]' : 'max-h-0',
          )}
        >
          <nav className="flex flex-col px-6 pb-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="font-net-label border-b border-white/10 py-4 text-sm text-white/90 transition-colors duration-300 hover:text-[var(--net-lime)]"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated &&
              authNavLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="font-net-label flex items-center gap-3 border-b border-white/10 py-4 text-sm text-white/90 transition-colors duration-300 hover:text-[var(--net-lime)]"
                  >
                    <Icon className="h-5 w-5" />
                    {t(link.labelKey, { defaultValue: link.fallback })}
                  </Link>
                );
              })}
            <div className="flex items-center gap-3 pt-5">
              <NetLanguageToggle />
              <NetAcademyHeaderAccountSection
                onNavigate={() => setMobileMenuOpen(false)}
              />
            </div>
          </nav>
        </div>
      </header>

      {!isLandingPage && <div className="h-[64px]" />}
    </>
  );
}
